import { useEffect, useRef, useState } from 'react';
import { Cookie, Settings, ShieldCheck } from 'lucide-react';

export const SALSA_COOKIE_CONSENT_KEY = 'salsa_cookie_consent';
export const OPEN_COOKIE_SETTINGS_EVENT = 'consltr:open-cookie-settings';

const LEGACY_CONSENT_STATUS_KEY = 'cookieConsent';
const LEGACY_COOKIE_SETTINGS_KEY = 'cookieSettings';

type CookieConsentStatus = 'accepted' | 'declined' | 'customized' | null;

export interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

type ConsentState = {
  status: CookieConsentStatus;
  settings: CookieSettings;
  needsMigration: boolean;
};

const initialConsentState: ConsentState = {
  status: null,
  settings: {
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  },
  needsMigration: false,
};

const requiredOnlySettings: CookieSettings = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

const allAcceptedSettings: CookieSettings = {
  necessary: true,
  analytics: true,
  marketing: false,
  functional: false,
};

const normalizeSettings = (value: unknown): CookieSettings | null => {
  if (!value || typeof value !== 'object') return null;

  const settings = value as Partial<CookieSettings>;
  return {
    necessary: true,
    analytics: settings.analytics === true,
    marketing: settings.marketing === true,
    functional: settings.functional === true,
  };
};

const parseSettings = (raw: string | null): CookieSettings | null => {
  if (!raw) return null;

  try {
    return normalizeSettings(JSON.parse(raw));
  } catch {
    return null;
  }
};

const inferStatus = (settings: CookieSettings): Exclude<CookieConsentStatus, null> => {
  if (settings.analytics) return 'accepted';
  if (!settings.analytics && !settings.marketing && !settings.functional) return 'declined';
  return 'customized';
};

const readInitialConsent = (): ConsentState => {
  try {
    const salsaSettings = parseSettings(window.localStorage.getItem(SALSA_COOKIE_CONSENT_KEY));
    if (salsaSettings) {
      return {
        status: inferStatus(salsaSettings),
        settings: salsaSettings,
        needsMigration: false,
      };
    }

    const legacyStatus = window.localStorage.getItem(
      LEGACY_CONSENT_STATUS_KEY
    ) as CookieConsentStatus;
    if (
      legacyStatus === 'accepted' ||
      legacyStatus === 'declined' ||
      legacyStatus === 'customized'
    ) {
      const legacySettings =
        parseSettings(window.localStorage.getItem(LEGACY_COOKIE_SETTINGS_KEY)) ??
        (legacyStatus === 'accepted' ? allAcceptedSettings : requiredOnlySettings);

      return {
        status: legacyStatus,
        settings: legacySettings,
        needsMigration: true,
      };
    }
  } catch {
    // Storage can be unavailable in hardened browser contexts. The banner still
    // works for the current page and the Salsa SDK remains cookieless by default.
  }

  return {
    status: null,
    settings: requiredOnlySettings,
    needsMigration: false,
  };
};

const storeConsent = (status: Exclude<CookieConsentStatus, null>, settings: CookieSettings) => {
  try {
    window.localStorage.setItem(SALSA_COOKIE_CONSENT_KEY, JSON.stringify(settings));
    // Keep the previous keys in sync so older pages do not show a second banner.
    window.localStorage.setItem(LEGACY_CONSENT_STATUS_KEY, status);
    window.localStorage.setItem(LEGACY_COOKIE_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // The consent event still updates the SDK for this page when storage is blocked.
  }
};

const notifySalsa = (settings: CookieSettings) => {
  window.dispatchEvent(
    new CustomEvent<CookieSettings>('salsa:consent-updated', {
      detail: settings,
    })
  );
};

const preferenceOptions: Array<{
  key: Exclude<keyof CookieSettings, 'necessary'>;
  name: string;
  description: string;
}> = [
  {
    key: 'analytics',
    name: 'Analytics',
    description: 'Measure visits and experiment performance with Salsa.',
  },
];

export const CookieConsent = () => {
  const [consent, setConsent] = useState<ConsentState>(initialConsentState);
  const [isClientReady, setIsClientReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const migrationNotified = useRef(false);
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const initialConsent = readInitialConsent();
    setConsent(initialConsent);
    setIsOpen(!initialConsent.status);
    setIsClientReady(true);

    const openSettings = () => {
      setShowSettings(true);
      setIsOpen(true);
      window.requestAnimationFrame(() => dialogRef.current?.focus());
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
  }, []);

  useEffect(() => {
    if (!consent.status || !consent.needsMigration || migrationNotified.current) return;

    migrationNotified.current = true;
    storeConsent(consent.status, consent.settings);
    notifySalsa(consent.settings);
  }, [consent]);

  const saveConsent = (status: Exclude<CookieConsentStatus, null>, settings: CookieSettings) => {
    storeConsent(status, settings);
    notifySalsa(settings);
    setConsent({ status, settings, needsMigration: false });
    setShowSettings(false);
    setIsOpen(false);
  };

  const updateSetting = (key: Exclude<keyof CookieSettings, 'necessary'>, value: boolean) => {
    setConsent(current => ({
      ...current,
      settings: { ...current.settings, [key]: value },
    }));
  };

  if (!isClientReady || !isOpen) return null;

  return (
    <section
      ref={dialogRef}
      role="dialog"
      tabIndex={-1}
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
      className="fixed inset-x-4 bottom-4 z-[70] mx-auto max-w-5xl rounded-3xl border border-line/15 bg-panel/95 p-5 shadow-2xl backdrop-blur-xl sm:p-6"
      data-agent-id="agent://consltr/privacy/cookie-consent"
    >
      <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <div className="mb-3 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-highlight text-black">
              <ShieldCheck aria-hidden="true" size={20} />
            </span>
            <div>
              <p className="section-kicker">Privacy controls</p>
              <h2 id="cookie-consent-title" className="serif text-2xl text-ink">
                Choose how Consltr measures your visit
              </h2>
            </div>
          </div>
          <p id="cookie-consent-description" className="text-sm leading-6 text-muted">
            Essential functionality is always available. Before you choose, Salsa sends aggregate
            events without cookies or persistent identifiers. If you allow analytics, Salsa uses a
            first-party visitor ID to measure site performance and keep A/B test assignments
            consistent. Choosing required only stops Salsa analytics. Read our{' '}
            <a
              href="/privacy/"
              className="font-semibold text-ink underline decoration-line/40 underline-offset-4"
            >
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href="/cookies/"
              className="font-semibold text-ink underline decoration-line/40 underline-offset-4"
            >
              Cookie Policy
            </a>
            .
          </p>
        </div>

        <div className="flex flex-wrap gap-2 sm:flex-nowrap">
          <button
            type="button"
            onClick={() => setShowSettings(current => !current)}
            aria-expanded={showSettings}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-line/15 px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-accent/60 hover:text-accent"
          >
            <Settings aria-hidden="true" size={16} />
            {showSettings ? 'Hide settings' : 'Customize'}
          </button>
          <button
            type="button"
            onClick={() => saveConsent('declined', requiredOnlySettings)}
            className="rounded-full border border-line/15 px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-accent/60"
          >
            Required only
          </button>
          <button
            type="button"
            onClick={() => saveConsent('accepted', allAcceptedSettings)}
            className="rounded-full bg-highlight px-5 py-2.5 text-sm font-bold text-black transition-transform hover:-translate-y-0.5"
          >
            Allow analytics
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="mt-5 border-t border-line/10 pt-5">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex items-center justify-between rounded-2xl bg-panel-alt p-4">
              <div>
                <p className="font-semibold text-ink">Essential</p>
                <p className="mt-1 text-xs text-muted">Security, navigation and saved consent.</p>
              </div>
              <span className="font-mono text-xs font-semibold uppercase tracking-wider text-accent">
                Always on
              </span>
            </div>

            {preferenceOptions.map(option => (
              <div
                key={option.key}
                className="flex items-center justify-between gap-4 rounded-2xl bg-panel-alt p-4"
              >
                <div>
                  <p id={`cookie-setting-${option.key}`} className="font-semibold text-ink">
                    {option.name}
                  </p>
                  <p
                    id={`cookie-setting-${option.key}-description`}
                    className="mt-1 text-xs leading-5 text-muted"
                  >
                    {option.description}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-labelledby={`cookie-setting-${option.key}`}
                  aria-describedby={`cookie-setting-${option.key}-description`}
                  aria-checked={consent.settings[option.key]}
                  onClick={() => updateSetting(option.key, !consent.settings[option.key])}
                  className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
                    consent.settings[option.key] ? 'bg-accent' : 'bg-line/20'
                  }`}
                >
                  <span
                    className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                      consent.settings[option.key] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => saveConsent('customized', consent.settings)}
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-bold text-canvas"
            >
              <Cookie aria-hidden="true" size={16} />
              Save choices
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
